import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import TenantService, { Location, CreateLocationPayload, UpdateLocationPayload } from '../services/TenantService';

interface LocationModalProps {
  isOpen: boolean;
  location: Location | null;
  onClose: () => void;
  onSave: () => void;
}

export default function LocationModal({
  isOpen,
  location,
  onClose,
  onSave,
}: LocationModalProps): React.JSX.Element | null {
  const [formData, setFormData] = useState<CreateLocationPayload>({
    name: '',
    street: '',
    street_number: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    radius_meters: 100,
    is_headquarters: false,
  });
  const [saving, setSaving] = useState(false);

  const isEdit = !!location;

  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name || '',
        street: location.street || '',
        street_number: location.house_number || location.houseNumber || location.street_number || '',
        postal_code: location.postal_code || location.postalCode || '',
        city: location.city || '',
        country: location.country || 'Deutschland',
        radius_meters: location.radius_meters ?? location.radiusMeters ?? 100,
        is_headquarters: location.is_headquarters || false,
      });
    } else {
      setFormData({
        name: '',
        street: '',
        street_number: '',
        postal_code: '',
        city: '',
        country: 'Deutschland',
        radius_meters: 100,
        is_headquarters: false,
      });
    }
  }, [location, isOpen]);

  const handleChange = (key: keyof CreateLocationPayload, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    setSaving(true);
    try {
      const apiPayload = {
        name: formData.name,
        street: formData.street,
        house_number: formData.street_number,
        postal_code: formData.postal_code,
        city: formData.city,
        country: formData.country,
        radius_meters: formData.radius_meters,
        is_headquarters: formData.is_headquarters,
      };

      if (isEdit && location) {
        await TenantService.updateLocation(location.id, apiPayload);
        toast.success('Standort aktualisiert');
      } else {
        await TenantService.createLocation(apiPayload);
        toast.success('Standort erstellt');
      }
      onSave();
    } catch (err: any) {
      console.error('Failed to save location:', err);
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.name?.[0] ||
        'Speichern fehlgeschlagen';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e3ef',
    borderRadius: '6px',
    fontSize: '14px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#444',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '560px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
            {isEdit ? 'Standort bearbeiten' : 'Neuer Standort'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#666',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              Name <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="z.B. Hauptsitz Berlin"
              style={inputStyle}
              required
            />
          </div>

          {/* Address Row */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>Strasse</label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => handleChange('street', e.target.value)}
                placeholder="Musterstrasse"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Hausnr.</label>
              <input
                type="text"
                value={formData.street_number}
                onChange={(e) => handleChange('street_number', e.target.value)}
                placeholder="123"
                style={inputStyle}
              />
            </div>
          </div>

          {/* PLZ + City */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>PLZ</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder="12345"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>Stadt</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="Berlin"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Country */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Land</label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => handleChange('country', e.target.value)}
              placeholder="Deutschland"
              style={inputStyle}
            />
          </div>

          {/* Radius */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Radius (Meter)</label>
            <input
              type="number"
              value={formData.radius_meters}
              onChange={(e) => handleChange('radius_meters', parseInt(e.target.value) || 0)}
              placeholder="100"
              min={0}
              style={inputStyle}
            />
            <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#888' }}>
              Radius fuer Geofencing / Zeiterfassung
            </p>
          </div>

          {/* Headquarters Checkbox */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <input
                type="checkbox"
                checked={formData.is_headquarters}
                onChange={(e) => handleChange('is_headquarters', e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              <span>Firmensitz (Headquarters)</span>
            </label>
            <p style={{ margin: '6px 0 0 28px', fontSize: '12px', color: '#888' }}>
              Nur ein Standort kann als Firmensitz markiert werden.
            </p>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 24px',
                border: '1px solid #e0e3ef',
                borderRadius: '10px',
                background: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '10px',
                background: saving ? '#ccc' : '#021a4a',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Speichern...' : isEdit ? 'Aktualisieren' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
